import { MapUtils } from '../../utility/map-utils.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { Result } from '../../utility/result.js';
import { ErrorLike } from '../core/error-like.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';

export class ArtificialSeatLockingSettings extends DataModel {
  static readonly TYPE_ID = '73d38b03c75d4481a504b20ed04d584c';

  static readonly RANDOMIZATION_STRENGTH_MIN = 1;
  static readonly RANDOMIZATION_STRENGTH_MAX = 100;

  static readonly FREE_SEAT_FRACTION_MIN = 0;
  static readonly FREE_SEAT_FRACTION_MAX = 1;

  private constructor(
    public readonly enabled: boolean,
    public readonly freeSeatFraction: number | null,
    public readonly randomizationStrength: number | null,
  ) {
    super();
  }

  get $typeId(): string {
    return ArtificialSeatLockingSettings.TYPE_ID;
  }

  static create(args: {
    enabled: boolean,
    freeSeatFraction: number | null,
    randomizationStrength: number | null,
  }): Result<ArtificialSeatLockingSettings, ErrorLike> {
    return Result.concat([
      this.validateFreeSeatFraction(args.freeSeatFraction),
      this.validateRandomizationStrength(args.randomizationStrength),
    ]).map(([freeSeatFraction, randomizationStrength]) => new ArtificialSeatLockingSettings(
        args.enabled,
        freeSeatFraction,
        randomizationStrength,
    ));
  }

  private static validateFreeSeatFraction(freeSeatFraction: number | null): Result<number | null, ErrorLike> {
    if(isNullOrUndefined(freeSeatFraction)) {
      return Result.ok(freeSeatFraction);
    }

    if(freeSeatFraction < this.FREE_SEAT_FRACTION_MIN || freeSeatFraction > this.FREE_SEAT_FRACTION_MAX) {
      return Result.error(`Free seat fraction must be in the range [${this.FREE_SEAT_FRACTION_MIN}, ${this.FREE_SEAT_FRACTION_MAX}].`);
    }

    return Result.ok(freeSeatFraction);
  }

  private static validateRandomizationStrength(randomizationStrength: number | null): Result<number | null, ErrorLike> {
    if(isNullOrUndefined(randomizationStrength)) {
      return Result.ok(randomizationStrength);
    }

    if(!Number.isSafeInteger(randomizationStrength)) {
      return Result.error('Randomization strength must be a safe integer or null.');
    }

    if(randomizationStrength < this.RANDOMIZATION_STRENGTH_MIN || randomizationStrength > this.RANDOMIZATION_STRENGTH_MAX) {
      return Result.error(`Randomization strength must be in the range [${this.RANDOMIZATION_STRENGTH_MIN}, ${this.RANDOMIZATION_STRENGTH_MAX}].`);
    }

    return Result.ok(randomizationStrength);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ArtificialSeatLockingSettings {
    return this.create({
      enabled: MapUtils.getBoolean(map, 'enabled'),
      freeSeatFraction: MapUtils.getNumberOrNull(map, 'freeSeatFraction'),
      randomizationStrength: MapUtils.getNumberOrNull(map, 'randomizationStrength'),
    }).unwrap();
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      enabled: this.enabled,
      freeSeatFraction: this.freeSeatFraction,
      randomizationStrength: this.randomizationStrength,
    };
  }

  clone(): this {
    return new ArtificialSeatLockingSettings(
        this.enabled,
        this.freeSeatFraction,
        this.randomizationStrength,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.enabled === this.enabled &&
      other.freeSeatFraction === this.freeSeatFraction &&
      other.randomizationStrength === this.randomizationStrength;
  }
}

export class ArtificialSeatLockingSettingsBuilder extends DataModelBuilder<ArtificialSeatLockingSettings> {
  build(context: SerializationContext, map: SerializedData): ArtificialSeatLockingSettings {
    return ArtificialSeatLockingSettings.fromMap(context, map);
  }
}
